import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { compact, isEmpty } from 'lodash';
import { ValuesType } from 'utility-types';
import { Button, DateFilters as EbscoDateFilters } from '@ebsco-ui/ebsco-ui';

import { FACET } from '@app/components';
import {
  DATE_FORMAT,
  DateRange,
  formatDateRange,
  FormattedDateRange,
  getDateRangeOptions,
  getInitialDateRange,
  validateDateRange,
} from '@app/utils';
import { dateFiltersMessages, sharedMessages } from '@app/translations';
import {
  PropsWithClassName,
  QUICK_FILTER_TYPE,
  SetState,
} from '@app/constants';

interface DateFiltersProps {
  ariaLabel: string;
  hasApplyButton?: boolean;
  onDateRangeChange: SetState<DateRange>;
  selectedDateRange: DateRange;
}

export const DateFilters: FC<PropsWithClassName<DateFiltersProps>> = ({
  ariaLabel,
  hasApplyButton = true,
  selectedDateRange,
  onDateRangeChange,
}) => {
  const initialDateRange = useMemo(getInitialDateRange, []);
  const { $t } = useIntl();
  const [selectedFilterType, setSelectedFilterType] = useState<
    keyof typeof QUICK_FILTER_TYPE
  >(QUICK_FILTER_TYPE.allTime);
  const [dateRange, setDateRange] = useState<FormattedDateRange>(
    getInitialDateRange()
  );
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);
  const quickFilters = useMemo(
    () =>
      Object.values(QUICK_FILTER_TYPE).map(filterType => ({
        label: $t(dateFiltersMessages[filterType]),
        value: filterType,
      })),
    [$t]
  );
  const {
    options: dateRangeOptions,
    invertedOptions: invertedDateRangeOptions,
  } = useMemo(getDateRangeOptions, []);

  const dateFiltersI18n = {
    groupTitle: ariaLabel,
    minDateLabel: $t(sharedMessages.from),
    maxDateLabel: $t(sharedMessages.to),
  };
  const isCustomRange = selectedFilterType === QUICK_FILTER_TYPE.custom;

  useEffect(() => {
    const currentSelectedFilterType =
      selectedDateRange.minDate &&
      invertedDateRangeOptions[selectedDateRange.minDate];

    if (currentSelectedFilterType) {
      setSelectedFilterType(currentSelectedFilterType);
    } else {
      setSelectedFilterType(QUICK_FILTER_TYPE.custom);

      if (selectedDateRange?.minDate || selectedDateRange?.maxDate) {
        setDateRange(formatDateRange(selectedDateRange, $t));
      }
    }

    if (isEmpty(selectedDateRange)) {
      setSelectedFilterType(QUICK_FILTER_TYPE.allTime);
    }
  }, [selectedDateRange, dateRangeOptions, $t, invertedDateRangeOptions]);

  const changeFilterType = (
    filterType: ValuesType<typeof QUICK_FILTER_TYPE>
  ) => {
    const dateFilterValue = dateRangeOptions[filterType];

    onDateRangeChange(dateFilterValue ? { minDate: dateFilterValue } : {});
    setDateRange(initialDateRange);
    setIsApplyButtonDisabled(false);
  };

  const changeDateRange = (customDateRange: Record<string, string>) => {
    const { hasDateError } = validateDateRange(customDateRange);
    const formattedDateRange = formatDateRange(customDateRange, $t);

    setSelectedFilterType(QUICK_FILTER_TYPE.custom);
    setDateRange(formatDateRange(customDateRange, $t));
    setIsApplyButtonDisabled(hasDateError);

    if (!hasApplyButton && !hasDateError) {
      onDateRangeChange({
        minDate: formattedDateRange.minDate?.value,
        maxDate: formattedDateRange.maxDate?.value,
      });
    }
  };

  const handleDateFiltersChange = ({ selectedFilter, customRange }) => {
    if (selectedFilter === QUICK_FILTER_TYPE.custom) {
      changeDateRange(customRange);
    } else if (selectedFilter !== selectedFilterType) {
      changeFilterType(selectedFilter);
    }
  };

  const handleApplyButtonClick = () => {
    onDateRangeChange({
      minDate: dateRange.minDate?.value,
      maxDate: dateRange.maxDate?.value,
    });
  };

  return (
    <>
      {compact([
        <EbscoDateFilters
          className="publication-filters"
          key={FACET.PUBLICATION_YEAR}
          i18n={dateFiltersI18n}
          dateRange={dateRange}
          isSharedError={dateRange.isSharedError}
          quickFilters={quickFilters}
          selectedFilter={selectedFilterType}
          showDateRange={isCustomRange}
          placeholder={DATE_FORMAT.YYYY}
          onChange={handleDateFiltersChange}
        />,
        hasApplyButton && isCustomRange && (
          <Button
            key="applyButton"
            disabled={isApplyButtonDisabled}
            onClick={handleApplyButtonClick}
          >
            {$t(sharedMessages.apply)}
          </Button>
        ),
      ])}
    </>
  );
};
